import { LayoutGrid, Plus, Settings } from 'lucide-react';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { NavLink, useLocation } from 'react-router';
import { TooltipContent } from '@/components/ui/tooltip';
import { navItemClasses, pairItemClasses, SHELL_SERVERS_LINK_ID } from './shell-classes';
import { shellLocation } from './shell-location';
import { useMediaQuery } from './use-media-query';

/** Below this width the nav links show their icons only; it matches the `min-[26rem]` label classes. */
const ICON_ONLY_QUERY = '(width < 26rem)';

/** Label text of a nav link: visually hidden below 26rem, where the link shows its icon only. */
const labelClasses = 'sr-only min-[26rem]:not-sr-only';

/**
 * Shows `label` as a tooltip on hover and keyboard focus while the nav is icon-only. The tooltip stays mounted at
 * every width, so the link is not remounted (and does not lose focus) when the width crosses the breakpoint.
 */
function IconOnlyTooltip({
  label,
  iconOnly,
  children,
}: {
  label: string;
  iconOnly: boolean;
  children: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  return (
    <TooltipPrimitive.Root open={iconOnly && open} onOpenChange={setOpen}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </TooltipPrimitive.Root>
  );
}

/**
 * The shell's primary navigation: Servers, Settings and Pair a server. Each link's name comes from its text, which
 * starts with the visible word at every width. Below 26rem all three links show their icons only, with the name as
 * a tooltip; from 26rem all three are labelled. Only the link of the exact page is current; Servers also brightens on
 * server pages without being marked current.
 */
export function PrimaryNav() {
  const { pathname } = useLocation();
  const inServers = shellLocation(pathname, '').inServers;
  const iconOnly = useMediaQuery(ICON_ONLY_QUERY);
  return (
    <nav aria-label="Main" className="min-w-0">
      <ul role="list" className="flex items-center gap-0.5 lg:flex-col lg:items-stretch">
        <li>
          <IconOnlyTooltip label="Servers" iconOnly={iconOnly}>
            <NavLink
              id={SHELL_SERVERS_LINK_ID}
              to="/"
              end
              data-section={inServers || undefined}
              className={navItemClasses}
            >
              <LayoutGrid aria-hidden />
              <span className={labelClasses}>Servers</span>
            </NavLink>
          </IconOnlyTooltip>
        </li>
        <li>
          <IconOnlyTooltip label="Settings" iconOnly={iconOnly}>
            <NavLink to="/settings" className={navItemClasses}>
              <Settings aria-hidden />
              <span className={labelClasses}>Settings</span>
            </NavLink>
          </IconOnlyTooltip>
        </li>
        <li className="lg:mt-2">
          <IconOnlyTooltip label="Pair a server" iconOnly={iconOnly}>
            <NavLink to="/pair" className={pairItemClasses}>
              <Plus aria-hidden />
              <span className={labelClasses}>
                Pair <span className="sr-only sm:not-sr-only">a server</span>
              </span>
            </NavLink>
          </IconOnlyTooltip>
        </li>
      </ul>
    </nav>
  );
}
